import React, { useMemo, useState } from 'react';
import { Delete as DeleteIcon, Video as VideoIcon, Info, Camera } from '@icon-park/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './styles/UsageMethod.css';

function UsageMethod({ data, onUpdate, templateVariant }) {
  const [uploadError, setUploadError] = useState('');
  const titleConfig = data.titleConfig || {};

  // 根据模板类型设置默认颜色
  const defaultBgColor = templateVariant === 'brown-v1' ? '#5C4A37' : '#1e90ff';
  const normalizeTitleConfig = (patch = {}) => {
    const next = { ...titleConfig, ...patch, enabled: true };
    if (templateVariant === 'brown-v1') {
      if (!next.bgColor || next.bgColor === '#1e90ff') {
        next.bgColor = '#5C4A37';
      }
      if (!next.textColor) {
        next.textColor = '#ffffff';
      }
    }
    return next;
  };
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ]
  };

  const formats = [
    'header', 'font',
    'bold', 'italic', 'underline',
    'color', 'background',
    'list', 'bullet',
    'align'
  ];

  const readVideoFile = (file) => {
    if (!file) return;
    // 简单校验：50MB以内
    const maxBytes = 50 * 1024 * 1024;
    if (file.size > maxBytes) {
      setUploadError('文件过大，限制为 50MB');
      return;
    }
    setUploadError('');
    const reader = new FileReader();
    reader.onload = (ev) => {
      onUpdate({ ...data, video: ev.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    readVideoFile(file);
  };

  const handleDropVideo = (e) => {
    e.preventDefault();
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    readVideoFile(file);
  };

  const handlePaste = (e) => {
    if (!e.clipboardData) return;
    const items = e.clipboardData.items || [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        readVideoFile(file);
        break;
      }
    }
  };

  const handlePosterUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const vo = data.videoOptions || {};
        onUpdate({ ...data, videoOptions: { ...vo, poster: e.target.result } });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteVideo = () => {
    onUpdate({ ...data, video: null });
  };

  // 解析高度字符串，拆分数值与单位
  const { heightValue, heightUnit } = useMemo(() => {
    const h = (data.videoStyle && data.videoStyle.height) || '';
    const m = String(h).match(/^(\d+(?:\.\d+)?)(px|vh|%)?$/);
    return {
      heightValue: m ? m[1] : '',
      heightUnit: m && m[2] ? m[2] : 'px'
    };
  }, [data.videoStyle && data.videoStyle.height]);

  const updateHeight = (val, unit) => {
    const v = val === '' ? '' : `${val}${unit}`;
    onUpdate({ ...data, videoStyle: { ...(data.videoStyle || {}), height: v } });
  };

  const handleColorReset = () => {
    onUpdate({ ...data, backgroundColor: defaultBgColor });
  };

  const handleColorTransparent = () => {
    onUpdate({ ...data, backgroundColor: 'transparent' });
  };

  return (
    <div className="usage-method-container">
      <div className="template-header-inline">
        <h3 className="template-title-inline">选择背景颜色</h3>
        <input
          type="color"
          value={data.backgroundColor || defaultBgColor}
          onChange={(e) => onUpdate({ ...data, backgroundColor: e.target.value })}
          className="color-input-inline"
        />
        <button type="button" className="color-action-btn" onClick={handleColorReset}>重置默认</button>
        <button type="button" className="color-action-btn" onClick={handleColorTransparent}>透明</button>
      </div>

      <div className="editor-section">
        <div className="editor-header-simple">
          <span className="editor-title-simple">标题</span>
        </div>

        <div className="title-config-grid">
          <div className="title-config-item">
            <label className="title-config-label">中文标题</label>
            <input
              type="text"
              className="title-config-input"
              value={titleConfig.chinese || ''}
              onChange={(e) => onUpdate({
                ...data,
                titleConfig: {
                  ...normalizeTitleConfig({ chinese: e.target.value })
                }
              })}
              placeholder="食用方法"
            />
          </div>
          <div className="title-config-item">
            <label className="title-config-label">英文标题（可选）</label>
            <input
              type="text"
              className="title-config-input"
              value={titleConfig.english || ''}
              onChange={(e) => onUpdate({
                ...data,
                titleConfig: {
                  ...normalizeTitleConfig({ english: e.target.value })
                }
              })}
              placeholder="Direction for Consumption."
            />
          </div>
        </div>
        
        <div className="quill-container">
          <ReactQuill
            theme="snow"
            value={data.title || ''}
            onChange={(value) => onUpdate({ ...data, title: value })}
            modules={modules}
            formats={formats}
            placeholder="Direction for Consumption. 食用方法"
          />
        </div>
      </div>

      {/* 视频配置区域 */}
      <div className="video-config-section">
        {/* 视频添加 */}
        <div className="config-card">
          <div className="config-card-header">
            <span className="config-card-title">视频添加</span>
          </div>
          <div className="config-card-body">
            {data.video ? (
              <div className="video-preview">
                <video 
                  src={data.video} 
                  controls={(data.videoOptions ? data.videoOptions.controls !== false : true)}
                  autoPlay={!!(data.videoOptions && data.videoOptions.autoplay)}
                  muted={!!(data.videoOptions && data.videoOptions.muted)}
                  loop={!!(data.videoOptions && data.videoOptions.loop)}
                  poster={data.videoOptions && data.videoOptions.poster ? data.videoOptions.poster : undefined}
                  className="video-player" 
                  style={{
                    borderRadius: data.videoStyle && data.videoStyle.radius ? data.videoStyle.radius : 0,
                    border: data.videoStyle && data.videoStyle.borderWidth ? `${data.videoStyle.borderWidth}px solid ${data.videoStyle.borderColor || '#e8eaed'}` : 'none',
                    height: data.videoStyle && data.videoStyle.height ? data.videoStyle.height : undefined
                  }}
                />
                <div className="video-actions">
                  <button className="video-action-btn" title="删除视频" onClick={handleDeleteVideo}>
                    <DeleteIcon theme="outline" size={14} fill="#fff" strokeWidth={3} /> 删除
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="video-upload-area"
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={handleDropVideo}
                onPaste={handlePaste}
              >
                <input
                  type="file"
                  id="usage-video-upload"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  style={{ display: 'none' }}
                />
                <label htmlFor="usage-video-upload" className="video-upload-box">
                  <div className="upload-icon-video"><VideoIcon theme="outline" size={32} fill="#1976d2" strokeWidth={3} /></div>
                  <div className="upload-text-video">上传视频</div>
                  <div className="upload-hint">支持拖拽/粘贴，mp4 等，≤ 50MB</div>
                </label>
                {uploadError && <div className="upload-error">{uploadError}</div>}
              </div>
            )}
          </div>
        </div>

        {/* 视频样式 */}
        <div className="config-card">
          <div className="config-card-header">
            <span className="config-card-title">视频样式</span>
          </div>
          <div className="config-card-body">
            <div className="video-style-grid">
              <div className="style-item">
                <label className="style-label">圆角</label>
                <div className="style-input-group">
                  <input 
                    type="number" 
                    className="style-input-number"
                    min="0" 
                    max="48" 
                    value={(data.videoStyle && data.videoStyle.radius) || 0} 
                    onChange={(e) => onUpdate({ ...data, videoStyle: { ...(data.videoStyle || {}), radius: Number(e.target.value) } })} 
                  />
                  <span className="style-unit">px</span>
                </div>
              </div>
              <div className="style-item">
                <label className="style-label">边框宽</label>
                <div className="style-input-group">
                  <input 
                    type="number" 
                    className="style-input-number"
                    min="0" 
                    max="12" 
                    value={(data.videoStyle && data.videoStyle.borderWidth) || 0} 
                    onChange={(e) => onUpdate({ ...data, videoStyle: { ...(data.videoStyle || {}), borderWidth: Number(e.target.value) } })} 
                  />
                  <span className="style-unit">px</span>
                </div>
              </div>
              <div className="style-item">
                <label className="style-label">边框颜色</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    className="style-color-picker"
                    value={(data.videoStyle && data.videoStyle.borderColor) || '#e8eaed'} 
                    onChange={(e) => onUpdate({ ...data, videoStyle: { ...(data.videoStyle || {}), borderColor: e.target.value } })} 
                  />
                  <span className="color-value-text">{(data.videoStyle && data.videoStyle.borderColor) || '#e8eaed'}</span>
                </div>
              </div>
            </div>
            <div className="height-control">
              <label className="style-label">高度</label>
              <div className="height-control-group">
                <div className="height-input-group">
                  <input
                    type="number"
                    min="0"
                    className="height-input-field"
                    placeholder="数值"
                    value={heightValue}
                    onChange={(e) => updateHeight(e.target.value, heightUnit)}
                  />
                  <select
                    className="height-unit-select"
                    value={heightUnit}
                    onChange={(e) => updateHeight(heightValue, e.target.value)}
                  >
                    <option value="px">px</option>
                    <option value="vh">vh</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div className="quick-chips">
                  <button type="button" className="quick-chip" onClick={() => updateHeight('', heightUnit)}>自适应</button>
                  <button type="button" className="quick-chip" onClick={() => onUpdate({ ...data, videoStyle: { ...(data.videoStyle || {}), height: '200px' } })}>200px</button>
                  <button type="button" className="quick-chip" onClick={() => onUpdate({ ...data, videoStyle: { ...(data.videoStyle || {}), height: '40vh' } })}>40vh</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 播放选项 */}
        <div className="config-card">
          <div className="config-card-header">
            <span className="config-card-title">播放选项</span>
          </div>
          <div className="config-card-body">
            <div className="playback-options-grid">
              <div className="playback-option-item">
                <span className="playback-label">显示控件</span>
                <label className="switch">
                  <input type="checkbox" checked={data.videoOptions ? data.videoOptions.controls !== false : true} onChange={(e) => onUpdate({ ...data, videoOptions: { ...(data.videoOptions || {}), controls: e.target.checked } })} />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="playback-option-item">
                <div className="playback-label-group">
                  <span className="playback-label">自动播放</span>
                  <span className="playback-hint" title="部分浏览器需静音才能自动播放"><Info theme="outline" size={14} fill="#666" /></span>
                </div>
                <label className="switch">
                  <input type="checkbox" checked={!!(data.videoOptions && data.videoOptions.autoplay)} onChange={(e) => {
                    const checked = e.target.checked;
                    const vo = { ...(data.videoOptions || {}), autoplay: checked };
                    if (checked && !vo.muted) vo.muted = true;
                    onUpdate({ ...data, videoOptions: vo });
                  }} />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="playback-option-item">
                <span className="playback-label">静音</span>
                <label className="switch">
                  <input type="checkbox" checked={!!(data.videoOptions && data.videoOptions.muted)} onChange={(e) => onUpdate({ ...data, videoOptions: { ...(data.videoOptions || {}), muted: e.target.checked } })} />
                  <span className="slider"></span>
                </label>
              </div>
              <div className="playback-option-item">
                <span className="playback-label">循环</span>
                <label className="switch">
                  <input type="checkbox" checked={!!(data.videoOptions && data.videoOptions.loop)} onChange={(e) => onUpdate({ ...data, videoOptions: { ...(data.videoOptions || {}), loop: e.target.checked } })} />
                  <span className="slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* 封面图 */}
        <div className="config-card">
          <div className="config-card-header">
            <span className="config-card-title">封面图</span>
          </div>
          <div className="config-card-body">
            <div className="poster-upload-area">
              <input 
                type="file" 
                id="poster-upload"
                accept="image/*" 
                onChange={handlePosterUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="poster-upload" className="poster-upload-btn">
                {data.videoOptions && data.videoOptions.poster ? (
                  <>
                    <img src={data.videoOptions.poster} alt="封面预览" className="poster-preview" />
                    <span className="poster-text">更换封面图</span>
                  </>
                ) : (
                  <>
                    <span className="poster-icon"><Camera theme="outline" size={20} fill="#666" /></span>
                    <span className="poster-text">选择文件</span>
                  </>
                )}
              </label>
              {data.videoOptions && data.videoOptions.poster && (
                <button 
                  type="button" 
                  className="poster-remove-btn"
                  onClick={() => {
                    const vo = { ...(data.videoOptions || {}), poster: null };
                    onUpdate({ ...data, videoOptions: vo });
                  }}
                >
                  移除
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UsageMethod;
